// L1Component.js - Version Optimisée Performance
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage, TASK_STATUS } from "../firebase.config.js";
import { useAgency } from '../AgencyContext';
import { useTaskTracker } from '../TaskTrack/useTaskTracker';
import "./L1Component.css";

import { FileUpload, Info } from "@mui/icons-material";
import GeneratePromptL1 from "./GeneratePromptL1.js";

// Cache pour les images préchargées
const imageCache = new Map();
const thumbnailCache = new Map();

const L1Component = ({ task, taskId, user, onTaskUpdate }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [thumbnails, setThumbnails] = useState(new Map());
  const [localImages, setLocalImages] = useState([]); // État local pour les images uploadées
  const fileInputRef = useRef(null);
  
  const { getAgencyDoc, currentAgency } = useAgency();
  const { trackEvent } = useTaskTracker();

  // Utiliser les images locales pour les calculs de statistiques
  const currentImageCount = localImages?.length || 0;
  const remainingImages = task.quantity - currentImageCount;
  const canUpload = currentImageCount < task.quantity;

  // Calculate stats for rejected/approved images
  const rejectedCount = localImages?.filter((img) => img.status === "rejected").length || 0;
  const approvedCount = localImages?.filter((img) => img.status === "approved").length || 0;
  const isAllUploaded = currentImageCount === task.quantity;
  const canComplete = isAllUploaded && rejectedCount === 0;

  // Check if there are QA comments to display - task-level comments from QA
  const qaComments = task.comments?.filter(comment => 
    comment.status && (
      comment.status.includes('TO_BE_REWORKED_L1') || 
      comment.status.includes('L1')
    )
  ).slice(-1) || [];

  // Créer des thumbnails pour les previews rapides
  const createThumbnail = useCallback((file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Taille réduite pour thumbnail
        const maxSize = 150;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      
      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Précharger toutes les images en arrière-plan
  const preloadImages = useCallback(async () => {
    if (!task.images) return;

    task.images.forEach(async (image) => {
      if (!imageCache.has(image.id) && !preloadedImages.has(image.id)) {
        try {
          const img = new Image();
          img.crossOrigin = "anonymous";
          
          img.onload = () => {
            imageCache.set(image.id, img);
            setPreloadedImages(prev => new Set([...prev, image.id]));
          };
          
          img.onerror = () => {
            console.warn(`Failed to preload image: ${image.id}`);
          };
          
          img.src = image.url;
        } catch (error) {
          console.warn(`Error preloading image ${image.id}:`, error);
        }
      }
    });
  }, [task.images, preloadedImages]);

  // Précharger les images au montage et quand elles changent
  useEffect(() => {
    preloadImages();
  }, [preloadImages]);

  // Auto-select first image when images are loaded avec optimisation
  useEffect(() => {
    if (localImages && localImages.length > 0 && !selectedImage) {
      const sortedImages = [...localImages].sort((a, b) => {
        const statusOrder = { rejected: 0, pending: 1, approved: 2 };
        return statusOrder[a.status || "pending"] - statusOrder[b.status || "pending"];
      });
      setSelectedImage(sortedImages[0]);
    }
  }, [localImages, selectedImage]);

  // Synchroniser les images locales avec les images de la tâche
  useEffect(() => {
    if (task.images) {
      setLocalImages(task.images);
    }
  }, [task.images]);


  // Mémoiser les images triées pour éviter les re-calculs
  const sortedImages = useMemo(() => {
    if (!localImages || localImages.length === 0) return [];
    return [...localImages].sort((a, b) => {
      const statusOrder = { rejected: 0, pending: 1, approved: 2 };
      return statusOrder[a.status || "pending"] - statusOrder[b.status || "pending"];
    });
  }, [localImages]);

  // Fonction pour générer un nom aléatoire au format AI_FACTORY_XXXXXXX
  const generateRandomFileName = () => {
    const randomNumber = Math.floor(Math.random() * 10000000); // Génère un nombre de 0 à 9999999
    const paddedNumber = randomNumber.toString().padStart(7, '0'); // Assure 7 chiffres avec des zéros au début
    return `AI_FACTORY_${paddedNumber}`;
  };

  const simulateUploadProgress = (fileName, duration = 3000) => {
    const steps = 20;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progressValue = Math.min(95, (currentStep / steps) * 100);

      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: progressValue,
      }));

      if (currentStep >= steps) {
        clearInterval(interval);
      }
    }, stepDuration);

    return interval;
  };

  const handleFileUpload = async (files) => {
    if (!canUpload) return;
    if (!files || files.length === 0) return;
    if (files.length > remainingImages) return;

    setUploading(true);
    setUploadProgress({});
    const uploadedImages = [];
    const errors = [];
    const progressIntervals = {};

    try {
      // Créer les thumbnails en parallèle pour les previews immédiates
      const thumbnailPromises = Array.from(files).map(async (file) => {
        if (file.type.startsWith("image/")) {
          const thumbnail = await createThumbnail(file);
          setThumbnails(prev => new Map([...prev, [file.name, thumbnail]]));
        }
      });
      
      await Promise.all(thumbnailPromises);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith("image/")) {
          errors.push(`${file.name} is not a valid image`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          errors.push(`${file.name} is too large (max 10MB)`);
          continue;
        }

        try {
          progressIntervals[file.name] = simulateUploadProgress(file.name);

          // Générer un nom aléatoire au format AI_FACTORY_XXXXXXX avec l'extension du fichier original
          const fileExtension = file.name.split('.').pop().toLowerCase();
          const randomName = generateRandomFileName();
          const fileName = `${randomName}.${fileExtension}`;

          const storageRef = ref(storage, `tasks/${taskId}/${fileName}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);

          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: 100,
          }));

          const imageData = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: randomName, // Utiliser le nom généré au lieu du nom original
            originalName: file.name, // Garder le nom original pour référence
            fileName: fileName,
            url: downloadURL,
            path: snapshot.ref.fullPath,
            uploadedBy: user.email,
            uploadedAt: new Date(),
            status: "pending",
            qaComment: "",
            size: file.size,
            type: file.type,
            isL2Image: false,
          };

          uploadedImages.push(imageData);

          // Précharger immédiatement la nouvelle image
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            imageCache.set(imageData.id, img);
            setPreloadedImages(prev => new Set([...prev, imageData.id]));
          };
          img.src = downloadURL;

        } catch (fileError) {
          console.error(`Error for ${file.name}:`, fileError);
          errors.push(`Error uploading ${file.name}`);

          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
        }
      }

      Object.values(progressIntervals).forEach((interval) => clearInterval(interval));

      if (uploadedImages.length > 0) {
        // Mise à jour immédiate de l'UI locale avant la sauvegarde
        const newLocalImages = [...localImages, ...uploadedImages];
        setLocalImages(newLocalImages);
        
        // Sélectionner automatiquement la première image uploadée si aucune sélection
        if (!selectedImage && uploadedImages.length > 0) {
          setSelectedImage(uploadedImages[0]);
        }
        
        const taskDocRef = getAgencyDoc('taskbacklog', taskId);
        if (!taskDocRef) {
          console.error("Could not get agency document reference for upload");
          return;
        }

        await updateDoc(taskDocRef, {
          images: arrayUnion(...uploadedImages),
          lastModified: serverTimestamp(),
          lastModifiedBy: user.email,
        });

        // Track the image upload
        const imageCount = uploadedImages.length;
        const imageText = imageCount === 1 ? 'media' : 'media';
        await trackEvent(taskId, task.taskName, `uploaded ${imageCount} ${imageText}`, currentAgency?.userRole || 'L1', task.quantity);

        await onTaskUpdate();
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      setUploadProgress({});
      // Nettoyer les thumbnails temporaires
      setThumbnails(new Map());
    }
  };

  // Optimiser la suppression avec mise à jour immédiate de l'UI
  const handleImageDelete = useCallback(async (imageToDelete) => {
    try {
      // Mise à jour immédiate de l'UI locale (optimistic update)
      const updatedImages = localImages.filter((img) => img.id !== imageToDelete.id);
      setLocalImages(updatedImages);
      
      // Si l'image sélectionnée est supprimée, sélectionner la suivante immédiatement
      if (selectedImage?.id === imageToDelete.id) {
        if (updatedImages.length > 0) {
          setSelectedImage(updatedImages[0]);
        } else {
          setSelectedImage(null);
        }
      }

      // Nettoyer le cache
      imageCache.delete(imageToDelete.id);
      setPreloadedImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageToDelete.id);
        return newSet;
      });

      // Opérations Firebase en arrière-plan
      const taskDocRef = getAgencyDoc('taskbacklog', taskId);
      if (!taskDocRef) {
        console.error("Could not get agency document reference for delete");
        return;
      }

      const deletePromises = [
        deleteObject(ref(storage, imageToDelete.path)),
        updateDoc(taskDocRef, {
          images: arrayRemove(imageToDelete),
          lastModified: serverTimestamp(),
          lastModifiedBy: user.email,
        })
      ];

      await Promise.all(deletePromises);
      await onTaskUpdate();

      // Track the image deletion event
      await trackEvent(taskId, task.taskName, 'deleted 1 image', currentAgency?.userRole || 'L1', task.quantity);

    } catch (error) {
      console.error("Delete error:", error);
      // En cas d'erreur, on pourrait revert l'UI ici
    }
  }, [localImages, selectedImage, taskId, user.email, onTaskUpdate, getAgencyDoc, trackEvent, task.taskName, task.quantity, currentAgency?.userRole]);

  // Optimiser la sélection d'image
  const handleImageSelect = useCallback((image) => {
    setSelectedImage(image);
    
    // Si l'image n'est pas encore préchargée, la charger immédiatement
    if (!imageCache.has(image.id)) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        imageCache.set(image.id, img);
        setPreloadedImages(prev => new Set([...prev, image.id]));
      };
      img.src = image.url;
    }
  }, []);

  const handleCompleteTask = async () => {
    if (!canComplete) return;

    try {
      const taskDocRef = getAgencyDoc('taskbacklog', taskId);
      if (!taskDocRef) {
        console.error("Could not get agency document reference");
        return;
      }

      // Count L1 images uploaded by current user
      const l1Images = task.images?.filter(img => !img.isL2Image && img.uploadedBy === user.email) || [];
      const totalL1Images = l1Images.length;

      // Update leaderboard stats for L1 user
      const updateData = {
        status: TASK_STATUS.PENDING_QA_L1,
        statusChangedAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email,
        assignedTo: null,
        assignedAt: null,
      };

      // Track L1 user stats for this step
      if (totalL1Images > 0) {
        const leaderboardStats = task.leaderboardStats || {};
        
        if (!leaderboardStats[user.email]) {
          leaderboardStats[user.email] = {
            role: 'L1',
            tasksCompleted: 0,
            imagesSubmitted: 0,
            imagesAccepted: 0,
            imagesRejected: 0,
            submissions: [],
            manager: task.createdBy || task.taskOwner || 'unknown'
          };
        }

        // Track this submission
        leaderboardStats[user.email].tasksCompleted += 1;
        leaderboardStats[user.email].imagesSubmitted += totalL1Images;
        leaderboardStats[user.email].submissions.push({
          timestamp: new Date(),
          phase: 'L1_TO_QA',
          imagesCount: totalL1Images,
          taskId: taskId
        });

        updateData.leaderboardStats = leaderboardStats;
      }

      await updateDoc(taskDocRef, updateData);

      // Track sending to QA
      await trackEvent(taskId, task.taskName, 'sent to QA', currentAgency?.userRole || 'L1', task.quantity);

      window.location.href = '/backlog';
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getImageStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "l1__image-badge--approved";
      case "rejected": return "l1__image-badge--rejected";
      default: return "l1__image-badge--pending";
    }
  };

  // Composant optimisé pour l'image de prévisualisation
  const PreviewImage = ({ image }) => {
    const cachedImage = imageCache.get(image.id);
    
    if (cachedImage) {
      // Image déjà en cache, affichage instantané avec magnifier
      return (
        <div className="l1__preview-container">
          <img 
            src={image.url} 
            alt={image.name} 
            className="l1__preview-image l1__preview-image--cached"
            style={{ opacity: 1 }}
          />
        </div>
      );
    }
    
    // Image pas encore chargée, utiliser une version optimisée avec magnifier
    return (
      <div className="l1__preview-container">
        <img 
          src={image.url} 
          alt={image.name} 
          className="l1__preview-image l1__preview-image--loading"
          loading="eager"
          decoding="async"
          onLoad={() => {
            const img = new Image();
            img.src = image.url;
            imageCache.set(image.id, img);
            setPreloadedImages(prev => new Set([...prev, image.id]));
          }}
        />
      </div>
    );
  };

  return (
    <div className="l1">
      <div className="l1__content">
        {/* Left Panel */}
        <div className="l1__left-panel">
          {/* Task Info Card */}
          <div className="l1__task-info-card" title={`${task.taskName}${task.description ? '\n\n' + task.description : ''}`}>
            <div className="l1__task-title">
              {task.taskName && task.taskName.length > 30 
                ? `${task.taskName.substring(0, 30)}...` 
                : task.taskName
              }
            </div>
            {task.description && (
              <div className="l1__task-description">
                {task.description.length > 60 
                  ? `${task.description.substring(0, 60)}...` 
                  : task.description
                }
              </div>
            )}
          </div>
          {/* Upload Button */}
          <button 
            className={`l1__upload-btn ${!canUpload ? 'l1__upload-btn--disabled' : ''}`}
            onClick={handleUploadClick}
            disabled={!canUpload}
          >
            <span className="l1__upload-icon">
              <FileUpload />
            </span> Upload · {currentImageCount}/{task.quantity}
          </button>

          {/* QA Comments */}
          {qaComments.length > 0 && (
            <div className="l1__qa-comments">
              {qaComments.map((comment, index) => (
                <div key={index} className="l1__qa-comment">
                  <div className="l1__qa-comment-header">
                    {comment.author || 'QA Team'}
                  </div>
                  <div className="l1__qa-comment-text">{comment.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* Rejection Warning */}
          {rejectedCount > 0 && (
            <div className="l1__rejection__warning">
              <div className="l1__rejection__warning__icon">
              <Info style={{scale: 0.7}}/> 
              </div>

              <div className="l1__rejection__warning__txt">
              <p>Delete and reupload content</p>
              </div>
           
            </div>
          )}

          {/* Upload Progress */}
          {uploading && Object.keys(uploadProgress).length > 0 && (
            <div className="l1__upload-progress">
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="l1__upload-progress-item">
                  <div 
                    className="l1__upload-progress-fill" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Image List Optimisée */}
          {sortedImages.length > 0 && (
            <div className="l1__image-list">
              {sortedImages.map((image) => (
                <div key={image.id} className="l1__image-item">
                  <div
                    className={`l1__image-badge ${getImageStatusClass(image.status)} ${
                      selectedImage?.id === image.id ? 'l1__image-badge--selected' : ''
                    } ${preloadedImages.has(image.id) ? 'l1__image-badge--loaded' : ''}`}
                    onClick={() => handleImageSelect(image)}
                  >
                    <span className="l1__image-badge-name">{image.name}</span>
                  
                    {image.status !== "approved" && (
                      <button
                        className="l1__image-badge-close"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageDelete(image);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  {/* Show rejection comment if image is rejected */}
                  {image.status === "rejected" && image.qaComment && (
                    <div className="l1__rejection-comment">
                      <div className="l1__rejection-comment-text">{image.qaComment}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFileUpload(Array.from(e.target.files));
                e.target.value = "";
              }
            }}
            style={{ display: "none" }}
          />
        </div>

        {/* Center Panel Optimisé */}
        <div className={`l1__center-panel ${selectedImage ? 'l1__center-panel--with-image' : ''}`}>
          {selectedImage ? (
            <PreviewImage image={selectedImage} />
          ) : (
            <div className="l1__center-placeholder">
              Upload images
            </div>
          )}
<GeneratePromptL1 
  task={task}
  taskId={taskId}
/>
          <div className="CTA__generate">
           
            
            <button
              className={`l1__send-qa-btn ${canComplete ? 'l1__send-qa-btn--active' : 'l1__send-qa-btn--disabled'}`}
              onClick={handleCompleteTask}
              disabled={!canComplete}
            >
              Send to QA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default L1Component;